import BackgroundCheckQ from '@/queries/BackgroundCheckQ'
import { BackgroundCheckStatus, DIALOGUES_CLUB_PRESIDENT_ROLE_SLUG } from '@/queries/constants'
import UserQ from '@/queries/UserQ'
import VolunteerRoleApplicationSubmissionQ from '@/queries/VolunteerRoleApplicationSubmissionQ'
import VolunteerRoleQ from '@/queries/VolunteerRoleQ'
import { SQL } from '@/client/query'
import { needsConsentForProgram } from '@/client/services/programs/helpers'
import { SeriesTag } from '@/client/services/tags/types'
import {
  CAW_101_TRAINING_SLUG,
  FINAL_EXAM_PREP_LEARNING_LABS_TRAINING_SLUG,
  LEARNING_LABS_101_TRAINING_SLUG,
  SAT_BOOTCAMP_101_TRAINING_SLUG,
  SAT_TIPS_TRAINING_SLUG,
} from '@/client/services/training'

import {
  AgeAssuranceCTA,
  AssentCTA,
  BackgroundCheckCTA,
  BackgroundCheckInProgressCTA,
  DialoguesClubPresidentApplyCTA,
  DialoguesSignUpCTA,
  GetCertifiedCTA,
  RequestParentConsentCTA,
  ReRequestParentConsentCTA,
} from '@/components/Worlds/Journey/CTAs'

import {
  ALGEBRA2_LEARNING_LABS_WORLD_SLUG,
  CERTIFICATION_WORLD_SLUGS,
  COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG,
  COLLEGE_LOUNGE_WORLD_SLUG,
  COLLEGE_PREP_WORLD_SLUG,
  DIALOGUES_WORLD_SLUG,
  ENRICHMENT_WORLD_SLUG,
  PRE_CALCULUS_LEARNING_LABS_WORLD_SLUG,
  SAT_BOOTCAMP_WORLD_SLUG,
  SAT_PREP_WORLD_SLUG,
  TORONTO_1_ON_1_WORLD_SLUG,
  TORONTO_GROUP_WORLD_SLUG,
} from '../constants'
import getWorldType from '../getWorldType'
import { LevelRequirementStatus } from './enum'
import {
  ADD_PHONE_NUMBER_TODO,
  APPLY_COACH_TODO,
  BECOME_INTERMEDIATE_PEER_REVIEWER_TODO,
  BECOME_TUTOR_ELSEWHERE_TODO,
  CAW_CURRICULUM_TRAINING_REQUIREMENT_GROUP,
  CAW_TUTOR_CONSENT_TODO,
  COLLEGE_VERIFY_TODO,
  COLLEGE_VERIFY_USA_TODO,
  COMPLETE_SAT_BOOTCAMP_ONBOARDING_TRAINING_TODO,
  createBecomeFullyCertifiedInCourseTodo,
  HOST_HIGH_QUALITY_SAT_BOOTCAMP_TODO,
  HOST_HIGHLY_ENGAGING_PROGRAMS_TODO,
  HOST_MIN_SESSIONS_CAW_TODO,
  HOST_SAT_BOOTCAMP_ONBOARDING_SESSION_TODO,
  HOST_TEN_HIGHLY_ENGAGING_SESSIONS_TODO,
  INVITE_ONLY_SUBJECT_TODO,
  LEADS_TRAINING_REQUIREMENT_GROUP,
  LEARNING_LABS_TUTOR_CONSENT_TODO,
} from './internal'
import getCertificationQuery from './internal/getCertificationQuery'
import type { LevelRequirement, WorldLevelRequirements } from './types'

const ASSENT_REQUIREMENT: LevelRequirement = {
  key: 'assent',
  type: 'todo',
  title: 'Give your permission',
  estimatedTimeMinutes: 1,
  getLevelStatusQueryFunction: ({ userId }) => SQL`
          (
            CASE
              WHEN ${UserQ.assent_completed_at} IS NOT NULL THEN ${LevelRequirementStatus.Complete}
              ELSE ${LevelRequirementStatus.Incomplete}
            END
          )
          ${UserQ.where({ id: userId })}
        `,
  Component: AssentCTA,
  alwaysUnlocked: true,
}

const PARENT_CONSENT_REQUIREMENT: LevelRequirement = {
  key: 'parent-consent',
  type: 'todo',
  title: 'Have your parent complete the consent form',
  estimatedTimeMinutes: 1,
  getLevelStatusQueryFunction: ({ userId }) => SQL`
        (
          CASE
            WHEN ${UserQ.parent_consent_completed_at} IS NOT NULL OR ${UserQ.parent_consent_waived_at} IS NOT NULL THEN ${LevelRequirementStatus.Complete}
            WHEN ${UserQ.parent_consent_requested_at} IS NOT NULL THEN ${LevelRequirementStatus.InProgress}
            ELSE ${LevelRequirementStatus.Incomplete}
          END
        )
        ${UserQ.where({ id: userId })}
      `,
  explanation: {
    [LevelRequirementStatus.InProgress]: 'Your parent has not yet completed the consent form.',
  },
  Component: RequestParentConsentCTA,
  InProgressComponent: ReRequestParentConsentCTA,
  alwaysUnlocked: true,
}

const PARENT_CONTACT_REQUIREMENT: LevelRequirement = {
  key: 'parent-contact',
  type: 'todo',
  title: 'Please enter your parent contact information',
  estimatedTimeMinutes: 1,
  getLevelStatusQueryFunction: ({ userId }) => SQL`
        (
          CASE
            WHEN ${UserQ.parent_details} IS NOT NULL THEN ${LevelRequirementStatus.Complete}
            ELSE ${LevelRequirementStatus.Incomplete}
          END
        )
        ${UserQ.where({ id: userId })}
      `,
  explanation: {
    [LevelRequirementStatus.InProgress]: 'You have not yet provided parent contact information.',
  },
  Component: RequestParentConsentCTA,
  alwaysUnlocked: true,
}

// This function is used to ensure that SAT Bootcamp requirements are a superset of SAT Prep requirements, so all tutors in the program are automatically tutors in the subject.
function getSATTutorRequirements({ worldSlug }: { worldSlug: string }): LevelRequirement[] {
  const { needsParentConsent, needsAssent } = needsConsentForProgram(SAT_BOOTCAMP_WORLD_SLUG)

  const isSATBootcamp = worldSlug === SAT_BOOTCAMP_WORLD_SLUG
  const bootcampConsentRequirements = []
  if (needsParentConsent) {
    bootcampConsentRequirements.push(PARENT_CONSENT_REQUIREMENT)
  } else {
    bootcampConsentRequirements.push(PARENT_CONTACT_REQUIREMENT)
  }
  if (needsAssent) {
    bootcampConsentRequirements.push(ASSENT_REQUIREMENT)
  }

  return [
    ...(isSATBootcamp
      ? // Extra bootcamp requirements that dont apply to SAT Prep
        [...bootcampConsentRequirements, ADD_PHONE_NUMBER_TODO]
      : []),
    COMPLETE_CERTIFICATION_REQUIREMENT,
    LEADS_TRAINING_REQUIREMENT_GROUP,
    isSATBootcamp
      ? {
          key: 'bootcamp-101',
          type: 'complete-training',
          slug: SAT_BOOTCAMP_101_TRAINING_SLUG,
          title: 'Complete SAT Bootcamp 101 training',
        }
      : // SAT Bootcamp tutors are automatically granted SAT Tips training
        {
          key: 'sat-tips-training',
          type: 'complete-training',
          slug: SAT_TIPS_TRAINING_SLUG,
          title: 'Complete SAT Tips Training',
        },
    BACKGROUND_CHECK_REQUIREMENT,
    AGE_ASSURANCE_REQUIREMENT,
  ]
}

const getSATPrepWorldRequirements: () => Partial<WorldLevelRequirements> = () => ({
  tutor: getSATTutorRequirements({ worldSlug: SAT_PREP_WORLD_SLUG }),
})

const getSATBootcampWorldRequirements: () => WorldLevelRequirements = () => ({
  member: [],
  tutor: getSATTutorRequirements({ worldSlug: SAT_BOOTCAMP_WORLD_SLUG }),
  'senior-tutor': [
    HOST_HIGH_QUALITY_SAT_BOOTCAMP_TODO,
    COMPLETE_SAT_BOOTCAMP_ONBOARDING_TRAINING_TODO,
    HOST_SAT_BOOTCAMP_ONBOARDING_SESSION_TODO,
    BECOME_INTERMEDIATE_PEER_REVIEWER_TODO,
  ],
  coach: [APPLY_COACH_TODO],
  moderator: [],
})

const TORONTO_GROUP_WORLD_REQUIREMENTS: Partial<WorldLevelRequirements> = {
  tutor: [INVITE_ONLY_SUBJECT_TODO, LEADS_TRAINING_REQUIREMENT_GROUP],
}

const TORONTO_1_ON_1_WORLD_REQUIREMENTS: Partial<WorldLevelRequirements> = {
  tutor: [INVITE_ONLY_SUBJECT_TODO, LEADS_TRAINING_REQUIREMENT_GROUP],
}

const DEFAULT_SCHOOL_WORLD_REQUIREMENTS: Partial<WorldLevelRequirements> = {
  tutor: [INVITE_ONLY_SUBJECT_TODO, ADD_PHONE_NUMBER_TODO, LEADS_TRAINING_REQUIREMENT_GROUP],
}

const ENRICHMENT_WORLD_REQUIREMENTS: Partial<WorldLevelRequirements> = {
  tutor: [LEADS_TRAINING_REQUIREMENT_GROUP, BECOME_TUTOR_ELSEWHERE_TODO],
}

const COLLEGE_LOUNGE_REQUIREMENTS: Partial<WorldLevelRequirements> = {
  tutor: [BECOME_TUTOR_ELSEWHERE_TODO],
}

const COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_REQUIREMENTS: Partial<WorldLevelRequirements> = {
  tutor: [
    CAW_TUTOR_CONSENT_TODO,
    COLLEGE_VERIFY_USA_TODO,
    ADD_PHONE_NUMBER_TODO,
    LEADS_TRAINING_REQUIREMENT_GROUP,
    {
      key: 'admissions-101',
      type: 'complete-training',
      slug: CAW_101_TRAINING_SLUG,
      title: 'Complete CAW 101 Training',
    },
    { ...CAW_CURRICULUM_TRAINING_REQUIREMENT_GROUP },
  ],
  'senior-tutor': [
    HOST_HIGHLY_ENGAGING_PROGRAMS_TODO({
      worldSlug: COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG,
      numSeriesRequired: 2,
      seriesTag: SeriesTag.HighlyEngagingCollegeAdmissionsWorkshop,
    }),
    HOST_MIN_SESSIONS_CAW_TODO,
    BECOME_INTERMEDIATE_PEER_REVIEWER_TODO,
  ],
  coach: [
    {
      ...APPLY_COACH_TODO,
      title:
        'Build an outstanding track record as a Tutor, and submit an application when recruitment opens',
    },
  ],
}

const PRE_CALCULUS_LEARNING_LABS_WORLD_REQUIREMENTS: Partial<WorldLevelRequirements> = {
  tutor: [
    LEARNING_LABS_TUTOR_CONSENT_TODO,
    ADD_PHONE_NUMBER_TODO,
    createBecomeFullyCertifiedInCourseTodo('Pre-Calculus'),
    LEADS_TRAINING_REQUIREMENT_GROUP,
    {
      key: 'learning-labs-101',
      type: 'complete-training',
      slug: LEARNING_LABS_101_TRAINING_SLUG,
      title: 'Complete Learning Labs 101 Training',
    },
  ],
  'senior-tutor': [
    HOST_HIGHLY_ENGAGING_PROGRAMS_TODO({
      worldSlug: PRE_CALCULUS_LEARNING_LABS_WORLD_SLUG,
      numSeriesRequired: 2,
      seriesTag: SeriesTag.HighlyEngagingPreCalculusLearningLab,
    }),
    BECOME_INTERMEDIATE_PEER_REVIEWER_TODO,
  ],
  coach: [
    {
      ...APPLY_COACH_TODO,
      title:
        'Build an outstanding track record as a Tutor, and submit an application when recruitment opens',
    },
  ],
}

const ALGEBRA2_LEARNING_LABS_WORLD_REQUIREMENTS: Partial<WorldLevelRequirements> = {
  tutor: [
    LEARNING_LABS_TUTOR_CONSENT_TODO,
    ADD_PHONE_NUMBER_TODO,
    createBecomeFullyCertifiedInCourseTodo('Algebra 2'),
    LEADS_TRAINING_REQUIREMENT_GROUP,
    {
      key: FINAL_EXAM_PREP_LEARNING_LABS_TRAINING_SLUG,
      type: 'complete-training',
      slug: FINAL_EXAM_PREP_LEARNING_LABS_TRAINING_SLUG,
      title: 'Complete Final Exam Prep Learning Labs Training',
      alwaysUnlocked: true,
    },
  ],
  'senior-tutor': [
    HOST_HIGHLY_ENGAGING_PROGRAMS_TODO({
      worldSlug: ALGEBRA2_LEARNING_LABS_WORLD_SLUG,
      numSeriesRequired: 2,
      seriesTag: SeriesTag.HighlyEngagingAlgebra2LearningLab,
    }),
    BECOME_INTERMEDIATE_PEER_REVIEWER_TODO,
  ],
  coach: [
    {
      ...APPLY_COACH_TODO,
      title:
        'Build an outstanding track record as a Tutor, and submit an application when recruitment opens',
    },
  ],
}

const COLLEGE_PREP_WORLD_REQUIREMENTS: Partial<WorldLevelRequirements> = {
  tutor: [COLLEGE_VERIFY_TODO, LEADS_TRAINING_REQUIREMENT_GROUP],
}

const DIALOGUES_WORLD_REQUIREMENTS: Partial<WorldLevelRequirements> = {
  tutor: [
    {
      key: 'age-15-18',
      type: 'todo',
      title: 'You are between ages 15–18',
      getLevelStatusQueryFunction: ({ userId }) => SQL<LevelRequirementStatus>`
        (
          CASE WHEN (
            SELECT
              ${UserQ.isAgeBetween(15, 18)}
            ${UserQ.where({ id: userId })}
          )
          THEN ${LevelRequirementStatus.Complete}
          ELSE ${LevelRequirementStatus.Locked}
          END
        )
      `,
      explanation: {
        [LevelRequirementStatus.Locked]:
          'You must be between ages 15–18 to become a Dialogues Club President.',
      },
    },
    BECOME_TUTOR_ELSEWHERE_TODO,
    {
      key: 'attend-dialogues-event',
      type: 'todo',
      title: 'Attend a Dialogues event',
      estimatedTimeMinutes: 60,
      getLevelStatusQueryFunction: ({ userId }) => SQL<LevelRequirementStatus>`
        (
          CASE WHEN ${UserQ.hasAttendedDialogues({ userId })}
          THEN ${LevelRequirementStatus.Complete}
          ELSE ${LevelRequirementStatus.Incomplete}
          END
        )
      `,
      Component: DialoguesSignUpCTA,
      explanation: {
        [LevelRequirementStatus.Incomplete]:
          'You must have attended a Dialogues event to become a Dialogues Club President.',
      },
    },
    {
      key: 'club-president-acceptance',
      type: 'todo',
      title: 'Get accepted as a Club President',
      getLevelStatusQueryFunction: ({ userId }) => SQL<LevelRequirementStatus>`
        (
          CASE
            WHEN
              EXISTS (
                SELECT 1
                ${UserQ.where(
                  { id: userId },
                  UserQ.hasActiveVolunteerRoleApplicationSubmission(
                    {
                      slug: DIALOGUES_CLUB_PRESIDENT_ROLE_SLUG,
                    },
                    VolunteerRoleApplicationSubmissionQ.isOfferAccepted()
                  )
                )}
              )
              -- we need this check so that tutors who were incumbents and granted the role without application are not locked out
              OR EXISTS (
                SELECT 1
                ${UserQ.where(
                  {
                    id: userId,
                  },
                  UserQ.hasActiveVolunteerRole({
                    slug: DIALOGUES_CLUB_PRESIDENT_ROLE_SLUG,
                  })
                )}
              )
            THEN ${LevelRequirementStatus.Complete}
            WHEN
              EXISTS (
                SELECT 1
                ${UserQ.where(
                  { id: userId },
                  UserQ.hasActiveVolunteerRoleApplicationSubmission({
                    slug: DIALOGUES_CLUB_PRESIDENT_ROLE_SLUG,
                  })
                )}
              )
            THEN ${LevelRequirementStatus.InProgress}
            WHEN
              EXISTS (
                SELECT 1
                ${VolunteerRoleQ.where(
                  {
                    slug: DIALOGUES_CLUB_PRESIDENT_ROLE_SLUG,
                  },
                  VolunteerRoleQ.isApplicationOpen()
                )}
              )
            THEN ${LevelRequirementStatus.Incomplete}
            ELSE ${LevelRequirementStatus.Locked}
          END
        )
      `,
      Component: DialoguesClubPresidentApplyCTA,
      explanation: {
        [LevelRequirementStatus.Locked]:
          'Club President applications are currently closed. Please check back later!',
        [LevelRequirementStatus.Incomplete]:
          'You can apply to become a Club President from the Volunteer Hub',
        [LevelRequirementStatus.InProgress]:
          'Your application is pending. Please check back later!',
      },
    },
  ],
}

function getWorldRequirementsOverride(worldSlug: string): Partial<WorldLevelRequirements> | null {
  switch (worldSlug) {
    case TORONTO_GROUP_WORLD_SLUG: {
      return TORONTO_GROUP_WORLD_REQUIREMENTS
    }
    case TORONTO_1_ON_1_WORLD_SLUG: {
      return TORONTO_1_ON_1_WORLD_REQUIREMENTS
    }
    case ENRICHMENT_WORLD_SLUG: {
      return ENRICHMENT_WORLD_REQUIREMENTS
    }
    case COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG: {
      return COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_REQUIREMENTS
    }
    case SAT_BOOTCAMP_WORLD_SLUG: {
      return getSATBootcampWorldRequirements()
    }
    case SAT_PREP_WORLD_SLUG: {
      return getSATPrepWorldRequirements()
    }
    case COLLEGE_LOUNGE_WORLD_SLUG: {
      return COLLEGE_LOUNGE_REQUIREMENTS
    }
    case COLLEGE_PREP_WORLD_SLUG: {
      return COLLEGE_PREP_WORLD_REQUIREMENTS
    }
    case ALGEBRA2_LEARNING_LABS_WORLD_SLUG: {
      return ALGEBRA2_LEARNING_LABS_WORLD_REQUIREMENTS
    }
    case PRE_CALCULUS_LEARNING_LABS_WORLD_SLUG: {
      return PRE_CALCULUS_LEARNING_LABS_WORLD_REQUIREMENTS
    }
    case DIALOGUES_WORLD_SLUG: {
      return DIALOGUES_WORLD_REQUIREMENTS
    }
    default: {
      return null
    }
  }
}

const AGE_ASSURANCE_REQUIREMENT: LevelRequirement = {
  key: 'age-assurance',
  type: 'todo',
  title: 'Complete Age Verification',
  getLevelStatusQueryFunction: ({ userId }) =>
    SQL<LevelRequirementStatus>`
      CASE
        WHEN (
          ${UserQ.hasRole('user:age_assurance:assured')}
          OR ${UserQ.hasRole('user:age_assurance:waived')}
        ) THEN ${LevelRequirementStatus.Complete}
        ELSE ${LevelRequirementStatus.Incomplete}
      END
      ${UserQ.where({ id: userId })}
    `,
  Component: AgeAssuranceCTA,
  InProgressComponent: AgeAssuranceCTA,
}

const BACKGROUND_CHECK_REQUIREMENT: LevelRequirement = {
  key: 'background-check',
  type: 'todo',
  title: 'Complete Background Check',
  estimatedTimeMinutes: 5,
  getLevelStatusQueryFunction: ({ userId, worldSlug }) =>
    SQL<LevelRequirementStatus>`
      CASE
      WHEN ${UserQ.canGetBackgroundCheck({ worldSlug })} THEN (
        CASE
        WHEN EXISTS(
          SELECT 1 ${BackgroundCheckQ.where(
            {
              userId: UserQ.id,
              limit: 1,
            },
            SQL`${BackgroundCheckQ.status} IN (
              ${BackgroundCheckStatus['in-review']},
              ${BackgroundCheckStatus.submitted}
            )`
          )}
        ) THEN ${LevelRequirementStatus.InProgress}
        WHEN EXISTS(
          SELECT 1 ${BackgroundCheckQ.where({
            userId: UserQ.id,
            status: BackgroundCheckStatus.completed,
            limit: 1,
          })}
        ) THEN ${LevelRequirementStatus.Complete}
        ELSE ${LevelRequirementStatus.Incomplete}
        END
      )
      ELSE ${LevelRequirementStatus.NotApplicable}
      END
      ${UserQ.where({ id: userId })}
    `,
  Component: BackgroundCheckCTA,
  InProgressComponent: BackgroundCheckInProgressCTA,
}

const COMPLETE_CERTIFICATION_REQUIREMENT: LevelRequirement = {
  key: 'certify-one',
  type: 'todo',
  title: 'Complete 1 Certification',
  getLevelStatusQueryFunction: ({ userId, worldSlug }) =>
    getCertificationQuery({ userId, worldSlug, variant: 'any' }),
  explanation: {
    [LevelRequirementStatus.InProgress]: 'Your certification is pending review.',
  },
  Component: GetCertifiedCTA,
  InProgressComponent: GetCertifiedCTA,
}

export default function getLevelRequirements({
  worldSlug,
}: {
  worldSlug: string
}): WorldLevelRequirements {
  const worldType = getWorldType({ worldSlug })

  const hasCertificationCourse = CERTIFICATION_WORLD_SLUGS.includes(worldSlug)

  const defaultRequirements = {
    member: [],
    tutor: hasCertificationCourse
      ? [
          COMPLETE_CERTIFICATION_REQUIREMENT,
          LEADS_TRAINING_REQUIREMENT_GROUP,
          AGE_ASSURANCE_REQUIREMENT,
          BACKGROUND_CHECK_REQUIREMENT,
        ]
      : [INVITE_ONLY_SUBJECT_TODO],
    'senior-tutor': [
      ...(hasCertificationCourse ? [createBecomeFullyCertifiedInCourseTodo()] : []),
      HOST_TEN_HIGHLY_ENGAGING_SESSIONS_TODO,
      BECOME_INTERMEDIATE_PEER_REVIEWER_TODO,
    ],
    coach: [APPLY_COACH_TODO],
    moderator: [],
  }

  const worldRequirementsOverride = getWorldRequirementsOverride(worldSlug)
  if (worldRequirementsOverride != null) {
    return {
      ...defaultRequirements,
      ...worldRequirementsOverride,
    }
  }

  if (worldType === 'school-partnership') {
    return { ...defaultRequirements, ...DEFAULT_SCHOOL_WORLD_REQUIREMENTS }
  }

  return defaultRequirements
}
